import { Link, useNavigate } from "react-router-dom";
import { Building, Building2, MapPin, Users } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { OrgLogo } from "@/components/common/Visuals";
import { OrgStatusBadges } from "@/components/common/Dialogs";
import { plural } from "@/lib/format";

export function OrgCard({ org, basePath = "/entreprises" }) {
  const navigate = useNavigate();
  const isTenants = basePath.startsWith("/locataires");
  return (
    <div data-testid={`org-card-${org.id}`} onClick={() => navigate(`${basePath}/${org.id}`)}
      className="lm-card lm-card-hover flex cursor-pointer flex-col p-5">
      <div className="flex items-start gap-4">
        <OrgLogo org={org} />
        <div className="min-w-0">
          <h3 className="truncate text-[17px] font-bold leading-snug">{org.name}</h3>
          <p className="mt-0.5 truncate text-sm text-stone-600">{[...org.org_type, ...org.sector].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
      {isTenants ? (
        org.bureau ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-stone-600" data-testid={`org-bureau-${org.id}`}>
            <Building className="h-3.5 w-3.5 text-stone-400 shrink-0" />
            <span className="font-medium text-stone-800">{org.bureau.toLowerCase().startsWith("bureau") ? org.bureau : `Bureau ${org.bureau}`}</span>
          </div>
        ) : null
      ) : (
        org.department.length > 0 && (
          <div className="mt-4 flex items-center gap-2 text-sm text-stone-500">
            <MapPin className="h-3.5 w-3.5 text-stone-400" />{org.department.join(", ")}
          </div>
        )
      )}
      <div className="mt-3"><OrgStatusBadges org={org} /></div>
      <div className="mt-3 flex flex-1 flex-wrap content-start gap-1.5">
        {org.expertises.slice(0, 3).map((t) => <span key={t} className="rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-700">{t}</span>)}
      </div>
      <div className="mt-4 flex items-center gap-2 border-t border-border/70 pt-3 text-sm font-semibold text-stone-600">
        <Users className="h-4 w-4 text-stone-400" /> {plural(org.contacts_count, "contact", "contacts")}
      </div>
    </div>
  );
}

export function OrgTable({ orgs, basePath = "/entreprises" }) {
  const navigate = useNavigate();
  const isTenants = basePath.startsWith("/locataires");
  return (
    <div className="lm-card overflow-hidden">
      <Table data-testid="orgs-table">
        <TableHeader>
          <TableRow className="bg-stone-50/80 hover:bg-stone-50/80">
            <TableHead>Nom</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Secteur</TableHead>
            <TableHead>{isTenants ? "Bureau" : "Département"}</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead className="text-right">Contacts</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orgs.map((o) => (
            <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`${basePath}/${o.id}`)} data-testid={`org-row-${o.id}`}>
              <TableCell>
                <div className="flex items-center gap-3"><OrgLogo org={o} size="sm" className="rounded-xl" /><span className="font-semibold">{o.name}</span></div>
              </TableCell>
              <TableCell className="text-stone-600">{o.org_type.join(", ")}</TableCell>
              <TableCell className="text-stone-600">{o.sector.join(", ")}</TableCell>
              <TableCell className="text-stone-600">{isTenants ? (o.bureau || "-") : o.department.join(", ")}</TableCell>
              <TableCell><OrgStatusBadges org={o} /></TableCell>
              <TableCell className="text-right font-semibold">{o.contacts_count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function OrgLinks({ orgs }) {
  if (!orgs?.length) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2">
      <Building2 className="h-4 w-4 text-stone-400" />
      {orgs.map((o, i) => (
        <Link key={o.id} to={`/entreprises/${o.id}`} className="font-semibold text-stone-900 underline-offset-4 hover:text-primary hover:underline" data-testid={`org-link-${o.id}`}>
          {o.name}{i < orgs.length - 1 ? "," : ""}
        </Link>
      ))}
    </span>
  );
}
